import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { company } from '../data/content';
import { PhoneIcon, MailIcon, ShieldIcon, YearsIcon, StarIcon, ClockIcon } from './icons';
const proofBar = [{
  icon: ShieldIcon,
  value: 'Licensed & Insured',
  label: 'Since day one — proof on request'
}, {
  icon: YearsIcon,
  value: '20 Years',
  label: 'Same family, same promise'
}, {
  icon: StarIcon,
  value: '9 out of 10',
  label: 'Customers rehire us'
}, {
  icon: ClockIcon,
  value: '3–4 Weeks',
  label: 'From cracked to resort-worthy'
}];
const ease = [0.23, 1, 0.32, 1] as const;
export function Hero() {
  const reduceMotion = useReducedMotion();
  return <section id="top" className="relative w-full overflow-hidden bg-navy-900" data-id="element-345" data-mp="nc86smc3fii0">
      <motion.img src={company.heroImage} alt="" aria-hidden="true" initial={{
      scale: 1.08
    }} animate={{
      scale: reduceMotion ? 1.08 : 1
    }} transition={{
      duration: 14,
      ease: 'linear'
    }} className="absolute inset-0 h-full w-full object-cover" data-id="element-346" data-mp="neauv0tor1k3" />
      <div aria-hidden="true" className="absolute inset-0 bg-navy-900/60 lg:bg-navy-900/15" data-id="element-347" data-mp="3j923tk0h7itf" />
      <div aria-hidden="true" className="absolute inset-y-0 left-0 w-full bg-navy-900/70 lg:w-[58%] lg:[mask-image:linear-gradient(to_right,black_45%,transparent)]" data-id="element-348" data-mp="3o73na61tdk5f" />

      <div className="relative mx-auto max-w-content px-5 pb-0 pt-16 lg:px-8 lg:pt-24" data-id="element-349" data-mp="3e8whmjcz6oiq">
        <motion.div initial="hidden" animate="visible" variants={{
        visible: {
          transition: {
            staggerChildren: 0.05
          }
        }
      }} className="max-w-3xl" data-id="element-350" data-mp="30iaweo01u2ho">
          <motion.p variants={{
          hidden: {
            opacity: 0,
            y: 12
          },
          visible: {
            opacity: 1,
            y: 0
          }
        }} transition={{
          duration: 0.28,
          ease
        }} className="inline-flex items-center gap-2.5 text-sm font-semibold text-sky-300" data-id="element-351" data-mp="2qk3hsbt8fwiu">
            <span className="h-px w-8 bg-sky-400" aria-hidden="true" data-id="element-352" data-mp="2lk8uei8o1p2j" />
            Family-owned pool professionals · Texas
          </motion.p>

          <motion.h1 variants={{
          hidden: {
            opacity: 0,
            y: 16
          },
          visible: {
            opacity: 1,
            y: 0
          }
        }} transition={{
          duration: 0.3,
          ease
        }} className="mt-5 font-display text-[2.6rem] font-extrabold leading-[0.98] tracking-tight text-white [text-shadow:0_2px_18px_rgba(6,20,38,0.6)] sm:text-6xl lg:text-7xl" data-id="element-353" data-mp="nbbev44vul7x">
            Your pool doesn&apos;t
            <br data-id="element-354" data-mp="162ccnou9oapw" />
            have to look
            <span className="relative ml-3 inline-block text-sky-300" data-id="element-355" data-mp="112g3zcamjv40">
              like that.
              <motion.span aria-hidden="true" initial={{
              scaleX: 0
            }} animate={{
              scaleX: 1
            }} transition={{
              duration: 0.3,
              delay: 0.32,
              ease
            }} style={{
              transformOrigin: 'left'
            }} className="absolute -bottom-1 left-0 h-1 w-full bg-sky-400" data-id="element-356" data-mp="x1efnqi8x3gt" />
            </span>
          </motion.h1>

          <motion.p variants={{
          hidden: {
            opacity: 0,
            y: 14
          },
          visible: {
            opacity: 1,
            y: 0
          }
        }} transition={{
          duration: 0.3,
          ease
        }} className="mt-7 max-w-xl text-lg leading-relaxed text-white/90 [text-shadow:0_1px_12px_rgba(6,20,38,0.65)]" data-id="element-357" data-mp="21aekbl6vatcq">
            Most pool owners don&apos;t fail their pools —{' '}
            <span className="font-semibold text-white" data-id="element-358" data-mp="2kxkihefskgs3">
              most contractors fail their pool owners.
            </span>{' '}
            For 20 years, A&amp;J has done plaster, tile, coping, spray decks, and remodels the way we&apos;d
            want it done if we were the customer.
          </motion.p>

          <motion.div variants={{
          hidden: {
            opacity: 0,
            y: 14
          },
          visible: {
            opacity: 1,
            y: 0
          }
        }} transition={{
          duration: 0.3,
          ease
        }} className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-359" data-mp="uoaj9nhxmxur">
            <a href={company.phoneHref} className="group inline-flex items-center justify-center gap-2.5 rounded-md bg-sky-400 px-7 py-4 font-display text-base font-bold text-navy-900 transition-[background-color,transform] duration-150 ease-out hover:-translate-y-0.5 hover:bg-sky-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-200" data-id="element-360" data-mp="e9jybwc3bdsy">
              <PhoneIcon className="h-5 w-5" data-id="element-361" data-mp="3cpcvip8jy95t" />
              Get a free estimate
            </a>
            <a href={company.emailHref} className="inline-flex items-center justify-center gap-2.5 rounded-md border border-white/30 bg-white/5 px-7 py-4 font-display text-base font-semibold text-white transition-colors duration-150 ease-out hover:border-sky-300 hover:text-sky-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-200" data-id="element-362" data-mp="3rhwureskzwhz">
              <MailIcon className="h-5 w-5" data-id="element-363" data-mp="19gkn521fo5ni" />
              Message us
            </a>
            <span className="text-sm text-navy-200 sm:ml-2" data-id="element-364" data-mp="1eaou3lap8dol">
              Call or text {company.phoneDisplay}
            </span>
          </motion.div>
        </motion.div>

        <motion.ul initial="hidden" animate="visible" variants={{
        visible: {
          transition: {
            staggerChildren: 0.04,
            delayChildren: 0.18
          }
        }
      }} className="mt-14 grid grid-cols-2 gap-px overflow-hidden rounded-t-lg bg-white/15 lg:mt-20 lg:grid-cols-4" data-id="element-365" data-mp="14klyddc70lmk">
          {proofBar.map(item => <motion.li key={item.value} variants={{
          hidden: {
            opacity: 0,
            y: 12
          },
          visible: {
            opacity: 1,
            y: 0
          }
        }} transition={{
          duration: 0.25,
          ease
        }} className="flex flex-col gap-2 bg-navy-900/90 px-5 py-6 backdrop-blur-sm sm:px-6" data-id="element-366" data-mp="1jajm5s67n9b8">
              <item.icon className="h-6 w-6 text-sky-300" data-id="element-367" data-mp="2eg91chfffz6r" />
              <p className="font-display text-lg font-bold leading-tight text-white" data-id="element-368" data-mp="29ir2jjv4j0ch">
                {item.value}
              </p>
              <p className="text-[13px] leading-snug text-navy-200" data-id="element-369" data-mp="24qpjnf3gabdh">{item.label}</p>
            </motion.li>)}
        </motion.ul>
      </div>
    </section>;
}